<template>
  <div class="fill-height page-back" style="overflow-y: auto;" >
    <v-container fluid class="pa-4">
      <!-- Header Section with Date Range -->
      <v-card class="mb-4 elevation-2">
        <v-card-text>
          <v-layout row wrap align-center>
            <v-flex xs12 md6>
              <div class="display-1 font-weight-bold">AI Usage Dashboard</div>
              <div class="subheading grey--text">Monitor AI consumption, costs, and performance metrics</div>
            </v-flex>

            <v-flex xs12 md6>
              <v-layout row wrap justify-end align-center>
                <v-menu
                  ref="startMenu"
                  v-model="startMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  min-width="150px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn small outline v-on="on" class="mr-1">
                      <v-icon small left>event</v-icon>
                      {{ dateRange.start | formatDateShort }}
                    </v-btn>
                  </template>
                  <v-date-picker v-model="dateRange.start" @input="startMenu = false"></v-date-picker>
                </v-menu>

                <span class="mx-2 grey--text">—</span>

                <v-menu
                  ref="endMenu"
                  v-model="endMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  min-width="150px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn small outline v-on="on" class="ml-1">
                      <v-icon small left>event</v-icon>
                      {{ dateRange.end | formatDateShort }}
                    </v-btn>
                  </template>
                  <v-date-picker v-model="dateRange.end" @input="endMenu = false"></v-date-picker>
                </v-menu>

                <v-btn small color="success" class="ml-3" @click="fetchData">
                  <v-icon small left>refresh</v-icon>
                  Refresh
                </v-btn>

                <v-btn small color="info" class="ml-2" @click="exportToExcel" :disabled="!stats.length">
                  <v-icon small left>get_app</v-icon>
                  Export Excel
                </v-btn>
                <v-btn small color="disabled" class="ml-2" v-if="isMasterAdmin" @click="adminFields = !adminFields" :disabled="!stats.length">
                  MA
                </v-btn>
              </v-layout>

              <v-layout row wrap justify-end class="mt-2">
                <v-btn-toggle v-model="quickRange" mandatory>
                  <v-btn small value="thisMonth" @click="setDateRange('thisMonth')">This Month</v-btn>
                  <v-btn small value="lastMonth" @click="setDateRange('lastMonth')">Last Month</v-btn>
                  <v-btn small value="last3Months" @click="setDateRange('last3Months')">3 Months</v-btn>
                  <v-btn small value="thisYear" @click="setDateRange('thisYear')">This Year</v-btn>
                </v-btn-toggle>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <!-- Loading Overlay -->
      <v-layout v-if="loading" row wrap class="my-5">
        <v-flex xs12 class="text-xs-center">
          <v-progress-circular indeterminate color="primary" :size="70"></v-progress-circular>
          <div class="mt-3 subheading">Loading usage data...</div>
        </v-flex>
      </v-layout>

      <template v-else>
        <!-- Summary Statistics Cards -->
        <v-layout row wrap class="mb-4">
          <v-flex xs12 sm6 md3 class="pa-2">
            <v-card class="elevation-3 fill-height" color="blue darken-2" dark>
              <v-card-text>
                <div class="text-xs-center">
                  <v-icon size="40" class="mb-2">bar_chart</v-icon>
                  <div class="caption">Total Requests</div>
                  <div class="display-1 font-weight-bold">{{ summaryStats.totalRequests | formatNumber }}</div>
                  <div class="caption" style="min-height: 20px;">&nbsp;</div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6 md3 class="pa-2">
            <v-card class="elevation-3 fill-height" color="green darken-2" dark>
              <v-card-text>
                <div class="text-xs-center">
                  <v-icon size="40" class="mb-2">memory</v-icon>
                  <div class="caption">Total Tokens</div>
                  <div class="display-1 font-weight-bold">{{ summaryStats.totalTokens | formatNumber }}</div>
                  <div class="caption" style="min-height: 20px;">{{ summaryStats.inputTokens | formatNumber }} in / {{ summaryStats.outputTokens | formatNumber }} out</div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6 md3 class="pa-2">
            <v-card class="elevation-3 fill-height" color="orange darken-2" dark>
              <v-card-text>
                <div class="text-xs-center">
                  <v-icon size="40" class="mb-2">payments</v-icon>
                  <div class="caption">Total Cost</div>
                  <div class="display-1 font-weight-bold">R{{ summaryStats.totalCost | formatCurrency }}</div>
                  <div class="caption" style="min-height: 20px;">
                    <span v-if="isMasterAdmin && adminFields">
                      Actual: R{{ summaryStats.actualTotalCost | formatCurrency }} |
                      Markup: R{{ summaryStats.totalMarkup | formatCurrency }}
                    </span>
                    <span v-else>&nbsp;</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6 md3 class="pa-2">
            <v-card class="elevation-3 fill-height" color="purple darken-2" dark>
              <v-card-text>
                <div class="text-xs-center">
                  <v-icon size="40" class="mb-2">trending_up</v-icon>
                  <div class="caption">Avg Cost / Request</div>
                  <div class="display-1 font-weight-bold">R{{ summaryStats.avgCostPerRequest | formatCurrency }}</div>
                  <div class="caption" style="min-height: 20px;">
                    <span v-if="isMasterAdmin && adminFields">Margin: {{ summaryStats.avgMarkupPercent | formatPercent }}</span>
                    <span v-else>&nbsp;</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Tabbed Detail View -->
        <v-card class="elevation-2">
          <v-tabs v-model="activeTab" color="white" slider-color="primary">
            <v-tab ripple>By User</v-tab>
            <v-tab ripple>By Model</v-tab>
            <v-tab ripple>By Workflow</v-tab>
            <v-tab ripple>Transaction Log</v-tab>

            <!-- By User Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-layout row wrap>
                    <v-flex xs12 md8 class="pa-2">
                      <v-data-table
                        :headers="userHeaders"
                        :items="userBreakdown"
                        :rows-per-page-items="[10, 25, 50]"
                        :pagination.sync="userPagination"
                        class="elevation-1"
                      >
                        <template v-slot:items="props">
                          <td>{{ props.item.user }}</td>
                          <td class="text-xs-right">{{ props.item.requests }}</td>
                          <td class="text-xs-right">{{ props.item.inputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.outputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.totalTokens | formatNumber }}</td>
                          <td class="text-xs-right">R{{ props.item.inputCost | formatCurrency }}</td>
                          <td class="text-xs-right">R{{ props.item.outputCost | formatCurrency }}</td>
                          <td class="text-xs-right font-weight-bold">R{{ props.item.totalCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.actualCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.markup | formatCurrency }}</td>
                        </template>
                      </v-data-table>
                    </v-flex>
                    <v-flex xs12 md4 class="pa-2">
                      <div class="subheading font-weight-bold mb-2" :class="isDarkMode ? '' : 'black--text'">Top Users by Cost</div>
                      <div style="position: relative; height: 300px;">
                        <canvas ref="userChart"></canvas>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- By Model Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-layout row wrap>
                    <v-flex xs12 md8 class="pa-2">
                      <v-data-table
                        :headers="modelHeaders"
                        :items="modelBreakdown"
                        :rows-per-page-items="[10, 25, 50]"
                        :pagination.sync="modelPagination"
                        class="elevation-1"
                      >
                        <template v-slot:items="props">
                          <td>{{ props.item.model }}</td>
                          <td v-if="isMasterAdmin && adminFields">{{ props.item.ecosystem }}</td>
                          <td class="text-xs-right">{{ props.item.requests }}</td>
                          <td class="text-xs-right">{{ props.item.inputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.outputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.totalTokens | formatNumber }}</td>
                          <td class="text-xs-right">R{{ props.item.inputCost | formatCurrency }}</td>
                          <td class="text-xs-right">R{{ props.item.outputCost | formatCurrency }}</td>
                          <td class="text-xs-right font-weight-bold">R{{ props.item.totalCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.actualCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.markup | formatCurrency }}</td>
                        </template>
                      </v-data-table>
                    </v-flex>
                    <v-flex xs12 md4 class="pa-2">
                      <div class="subheading font-weight-bold mb-2" :class="isDarkMode ? '' : 'black--text'">Model Distribution</div>
                      <div style="position: relative; height: 300px;">
                        <canvas ref="modelChart"></canvas>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- By Workflow Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-layout row wrap>
                    <v-flex xs12 md8 class="pa-2">
                      <v-data-table
                        :headers="workflowHeaders"
                        :items="workflowBreakdown"
                        :rows-per-page-items="[10, 25, 50]"
                        :pagination.sync="workflowPagination"
                        class="elevation-1"
                      >
                        <template v-slot:items="props">
                          <td>{{ props.item.workflow }}</td>
                          <td class="text-xs-right">{{ props.item.requests }}</td>
                          <td class="text-xs-right">{{ props.item.inputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.outputTokens | formatNumber }}</td>
                          <td class="text-xs-right">{{ props.item.totalTokens | formatNumber }}</td>
                          <td class="text-xs-right">R{{ props.item.inputCost | formatCurrency }}</td>
                          <td class="text-xs-right">R{{ props.item.outputCost | formatCurrency }}</td>
                          <td class="text-xs-right font-weight-bold">R{{ props.item.totalCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.actualCost | formatCurrency }}</td>
                          <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.markup | formatCurrency }}</td>
                        </template>
                      </v-data-table>
                    </v-flex>
                    <v-flex xs12 md4 class="pa-2">
                      <div class="subheading font-weight-bold mb-2" :class="isDarkMode ? '' : 'black--text'">Top Workflows by Cost</div>
                      <div style="position: relative; height: 300px;">
                        <canvas ref="workflowChart"></canvas>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Transaction Log Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-text-field
                    v-model="search"
                    append-icon="search"
                    label="Search transactions"
                    single-line
                    hide-details
                    class="mb-3"
                  ></v-text-field>
                  <v-data-table
                    :headers="transactionHeaders"
                    :items="stats"
                    :search="search"
                    :rows-per-page-items="[15, 30, 50, 100]"
                    :pagination.sync="transactionPagination"
                    class="elevation-1"
                  >
                    <template v-slot:items="props">
                      <td>{{ props.item.createdAt | formatDate }}</td>
                      <td>{{ props.item.user }}</td>
                      <td>{{ props.item.workflow }}</td>
                      <td>{{ props.item.ecosystem }}</td>
                      <td>{{ props.item.model }}</td>
                      <td class="text-xs-right">{{ props.item.input | formatNumber }}</td>
                      <td class="text-xs-right">{{ props.item.output | formatNumber }}</td>
                      <td class="text-xs-right">R{{ props.item.inputCost | formatCurrency }}</td>
                      <td class="text-xs-right">R{{ props.item.outputCost | formatCurrency }}</td>
                      <td class="text-xs-right font-weight-bold">R{{ props.item.totalCost | formatCurrency }}</td>
                      <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.actualCost | formatCurrency }}</td>
                      <td v-if="isMasterAdmin && adminFields" class="text-xs-right">R{{ props.item.markup | formatCurrency }}</td>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import {endOfMonth, startOfMonth, subMonths, startOfYear, format, parse} from "date-fns";
import Chart from 'chart.js/dist/Chart.js';
import xlsx from 'json-as-xlsx';

export default {
  data() {
    return {
      loading: false,
      stats: [],
      dateRange: {
        start: format(startOfMonth(new Date()), 'YYYY-MM-DD'),
        end: format(endOfMonth(new Date()), 'YYYY-MM-DD')
      },
      startMenu: false,
      endMenu: false,
      activeTab: null,
      search: '',
      charts: {
        ecosystem: null,
        user: null,
        model: null,
        workflow: null
      },
      userPagination: { rowsPerPage: 10 },
      modelPagination: { rowsPerPage: 10 },
      workflowPagination: { rowsPerPage: 10 },
      transactionPagination: { rowsPerPage: 15 },
      quickRange: 'thisMonth',
      adminFields: false
    }
  },
  mounted() {
    // Delay initial fetch to prevent flickering
    setTimeout(() => {
      this.fetchData();
    }, 100);
  },
  beforeDestroy() {
    // Destroy all charts to prevent memory leaks
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
  },
  watch: {
    stats: {
      handler() {
        // Delay chart rendering to ensure DOM is ready and prevent flickering
        setTimeout(() => {
          this.renderAllCharts();
        }, 100);
      },
      deep: true
    }
  },
  methods: {
    startOfMonth,
    endOfMonth,
    format,

    setDateRange(range) {
      const now = new Date();
      let lastMonth;

      switch(range) {
        case 'thisMonth':
          this.dateRange.start = format(startOfMonth(now), 'YYYY-MM-DD');
          this.dateRange.end = format(endOfMonth(now), 'YYYY-MM-DD');
          break;
        case 'lastMonth':
          lastMonth = subMonths(now, 1);
          this.dateRange.start = format(startOfMonth(lastMonth), 'YYYY-MM-DD');
          this.dateRange.end = format(endOfMonth(lastMonth), 'YYYY-MM-DD');
          break;
        case 'last3Months':
          this.dateRange.start = format(subMonths(now, 3), 'YYYY-MM-DD');
          this.dateRange.end = format(now, 'YYYY-MM-DD');
          break;
        case 'thisYear':
          this.dateRange.start = format(startOfYear(now), 'YYYY-MM-DD');
          this.dateRange.end = format(now, 'YYYY-MM-DD');
          break;
      }
      this.fetchData();
    },

    async fetchData() {
      try {
        this.loading = true;
        const start = parse(this.dateRange.start);
        const end = parse(this.dateRange.end);
        this.stats = (await this.$newReq('POST', `ai/admin/setup/stats`, {start, end})).map(v => ({
          ...v,
          createdAt: new Date(v.createdAt)
        }));
      } catch (e) {
        console.log(e);
        this.$snack.networkError();
      } finally {
        this.loading = false;
      }
    },

    async getUsageData(start, end) {
      try {
        this.loading = true;
        this.stats = await this.$newReq('POST', `ai/admin/setup/stats`, {start, end});
      } catch (e) {
        console.log(e);
        this.$snack.networkError();
      } finally {
        this.loading = false;
      }
    },

    exportToExcel() {
      const dateRangeStr = `${this.dateRange.start} to ${this.dateRange.end}`;
      const fileName = `AI_Usage_Report_${format(new Date(), 'YYYY-MM-DD_HHmmss')}`;

      // Prepare Summary Data
      const summaryData = [{
        'Metric': 'Total Requests',
        'Value': this.summaryStats.totalRequests
      }, {
        'Metric': 'Total Tokens',
        'Value': this.summaryStats.totalTokens
      }, {
        'Metric': 'Input Tokens',
        'Value': this.summaryStats.inputTokens
      }, {
        'Metric': 'Output Tokens',
        'Value': this.summaryStats.outputTokens
      }, {
        'Metric': 'Total Cost (R)',
        'Value': parseFloat(this.summaryStats.totalCost).toFixed(2)
      }, {
        'Metric': 'Average Cost per Request (R)',
        'Value': parseFloat(this.summaryStats.avgCostPerRequest).toFixed(2)
      }];

      if (this.isMasterAdmin && this.adminFields) {
        summaryData.push(
          { 'Metric': 'Actual Total Cost (R)', 'Value': parseFloat(this.summaryStats.actualTotalCost).toFixed(2) },
          { 'Metric': 'Total Markup (R)', 'Value': parseFloat(this.summaryStats.totalMarkup).toFixed(2) },
          { 'Metric': 'Average Markup (%)', 'Value': parseFloat(this.summaryStats.avgMarkupPercent).toFixed(1) }
        );
      }

      // Prepare User Breakdown Data
      const userData = this.userBreakdown.map(user => {
        const row = {
          'User': user.user,
          'Requests': user.requests,
          'Input Tokens': user.inputTokens,
          'Output Tokens': user.outputTokens,
          'Total Tokens': user.totalTokens,
          'Input Cost (R)': parseFloat(user.inputCost).toFixed(2),
          'Output Cost (R)': parseFloat(user.outputCost).toFixed(2),
          'Total Cost (R)': parseFloat(user.totalCost).toFixed(2)
        };
        if (this.isMasterAdmin && this.adminFields) {
          row['Actual Cost (R)'] = parseFloat(user.actualCost).toFixed(2);
          row['Markup (R)'] = parseFloat(user.markup).toFixed(2);
        }
        return row;
      });

      // Prepare Model Breakdown Data
      const modelData = this.modelBreakdown.map(model => {
        const row = {
          'Model': model.model
        };

        if (this.isMasterAdmin && this.adminFields) {
          row['Ecosystem'] = model.ecosystem;
        }

        row['Requests'] = model.requests;
        row['Input Tokens'] = model.inputTokens;
        row['Output Tokens'] = model.outputTokens;
        row['Total Tokens'] = model.totalTokens;
        row['Input Cost (R)'] = parseFloat(model.inputCost).toFixed(2);
        row['Output Cost (R)'] = parseFloat(model.outputCost).toFixed(2);
        row['Total Cost (R)'] = parseFloat(model.totalCost).toFixed(2);

        if (this.isMasterAdmin && this.adminFields) {
          row['Actual Cost (R)'] = parseFloat(model.actualCost).toFixed(2);
          row['Markup (R)'] = parseFloat(model.markup).toFixed(2);
        }
        return row;
      });

      // Prepare Workflow Breakdown Data
      const workflowData = this.workflowBreakdown.map(workflow => {
        const row = {
          'Workflow': workflow.workflow,
          'Requests': workflow.requests,
          'Input Tokens': workflow.inputTokens,
          'Output Tokens': workflow.outputTokens,
          'Total Tokens': workflow.totalTokens,
          'Input Cost (R)': parseFloat(workflow.inputCost).toFixed(2),
          'Output Cost (R)': parseFloat(workflow.outputCost).toFixed(2),
          'Total Cost (R)': parseFloat(workflow.totalCost).toFixed(2)
        };
        if (this.isMasterAdmin && this.adminFields) {
          row['Actual Cost (R)'] = parseFloat(workflow.actualCost).toFixed(2);
          row['Markup (R)'] = parseFloat(workflow.markup).toFixed(2);
        }
        return row;
      });

      // Prepare Ecosystem Breakdown Data
      const ecosystemData = this.ecosystemBreakdown.map(eco => ({
        'Ecosystem': eco.ecosystem,
        'Requests': eco.requests,
        'Total Tokens': eco.tokens,
        'Total Cost (R)': parseFloat(eco.cost).toFixed(2)
      }));

      // Prepare Transaction Log Data
      const transactionData = this.stats.map(transaction => {
        const row = {
          'Date': format(new Date(transaction.createdAt), 'YYYY-MM-DD HH:mm'),
          'User': transaction.user,
          'Workflow': transaction.workflow,
          'Field': transaction.field,
          'Ecosystem': transaction.ecosystem,
          'Model': transaction.model,
          'Input Tokens': transaction.input,
          'Output Tokens': transaction.output,
          'Input Cost (R)': parseFloat(transaction.inputCost).toFixed(2),
          'Output Cost (R)': parseFloat(transaction.outputCost).toFixed(2),
          'Total Cost (R)': parseFloat(transaction.totalCost).toFixed(2)
        };
        if (this.isMasterAdmin && this.adminFields) {
          row['Actual Cost (R)'] = parseFloat(transaction.actualCost).toFixed(2);
          row['Markup (R)'] = parseFloat(transaction.markup).toFixed(2);
        }
        return row;
      });

      // Create sheets array
      const data = [
        {
          sheet: 'Summary',
          columns: [
            { label: 'Metric', value: 'Metric' },
            { label: 'Value', value: 'Value' }
          ],
          content: summaryData
        },
        {
          sheet: 'By User',
          columns: Object.keys(userData[0] || {}).map(key => ({ label: key, value: key })),
          content: userData
        },
        {
          sheet: 'By Model',
          columns: Object.keys(modelData[0] || {}).map(key => ({ label: key, value: key })),
          content: modelData
        },
        {
          sheet: 'By Workflow',
          columns: Object.keys(workflowData[0] || {}).map(key => ({ label: key, value: key })),
          content: workflowData
        },
        {
          sheet: 'By Ecosystem',
          columns: Object.keys(ecosystemData[0] || {}).map(key => ({ label: key, value: key })),
          content: ecosystemData
        },
        {
          sheet: 'Transaction Log',
          columns: Object.keys(transactionData[0] || {}).map(key => ({ label: key, value: key })),
          content: transactionData
        }
      ];

      // Download the Excel file
      xlsx(data, {
        fileName: fileName,
        extraLength: 3,
        writeOptions: {}
      });

      this.$snack.info('Excel report exported successfully!');
    },

    renderAllCharts() {
      if (this.loading || !this.stats.length) return;

      this.$nextTick(() => {
        this.renderEcosystemChart();
        this.renderUserChart();
        this.renderModelChart();
        this.renderWorkflowChart();
      });
    },

    aggregateBy(key) {
      const grouped = {};
      this.stats.forEach(item => {
        const groupKey = item[key];
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            [key]: groupKey,
            requests: 0,
            inputTokens: 0,
            outputTokens: 0,
            totalTokens: 0,
            inputCost: 0,
            outputCost: 0,
            totalCost: 0,
            actualCost: 0,
            markup: 0
          };
        }
        grouped[groupKey].requests++;
        grouped[groupKey].inputTokens += item.input || 0;
        grouped[groupKey].outputTokens += item.output || 0;
        grouped[groupKey].totalTokens += (item.input || 0) + (item.output || 0);
        grouped[groupKey].inputCost += item.inputCost || 0;
        grouped[groupKey].outputCost += item.outputCost || 0;
        grouped[groupKey].totalCost += item.totalCost || 0;
        grouped[groupKey].actualCost += item.actualCost || 0;
        grouped[groupKey].markup += item.markup || 0;
      });
      return Object.values(grouped).sort((a, b) => b.totalCost - a.totalCost);
    },

    renderEcosystemChart() {
      if (!this.$refs.ecosystemChart) return;

      // Destroy existing chart
      if (this.charts.ecosystem) {
        this.charts.ecosystem.destroy();
        this.charts.ecosystem = null;
      }

      const ctx = this.$refs.ecosystemChart.getContext('2d');
      const data = this.ecosystemBreakdown;

      if (!data.length) return;

      this.charts.ecosystem = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.map(d => d.ecosystem),
          datasets: [{
            data: data.map(d => d.cost),
            backgroundColor: [
              '#4CAF50',
              '#2196F3',
              '#FF9800',
              '#9C27B0',
              '#F44336',
              '#00BCD4'
            ],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 500
          },
          legend: {
            position: 'right',
            labels: {
              padding: 10,
              fontSize: 12
            }
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                return label + ': R' + value.toFixed(2);
              }
            }
          }
        }
      });
    },

    renderUserChart() {
      if (!this.$refs.userChart) return;

      // Destroy existing chart
      if (this.charts.user) {
        this.charts.user.destroy();
        this.charts.user = null;
      }

      const ctx = this.$refs.userChart.getContext('2d');
      const topUsers = this.userBreakdown.slice(0, 5);

      if (!topUsers.length) return;

      this.charts.user = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: topUsers.map(d => d.user),
          datasets: [{
            label: 'Total Cost (R)',
            data: topUsers.map(d => d.totalCost),
            backgroundColor: '#2196F3',
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 500
          },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return 'R' + value.toFixed(2);
                }
              }
            }],
            yAxes: [{
              ticks: {
                fontSize: 11
              }
            }]
          },
          legend: {
            display: false
          }
        }
      });
    },

    renderModelChart() {
      if (!this.$refs.modelChart) return;

      // Destroy existing chart
      if (this.charts.model) {
        this.charts.model.destroy();
        this.charts.model = null;
      }

      const ctx = this.$refs.modelChart.getContext('2d');
      const data = this.modelBreakdown.slice(0, 8); // Limit to top 8 models

      if (!data.length) return;

      this.charts.model = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: data.map(d => d.model),
          datasets: [{
            data: data.map(d => d.requests),
            backgroundColor: [
              '#4CAF50',
              '#2196F3',
              '#FF9800',
              '#9C27B0',
              '#F44336',
              '#00BCD4',
              '#FFEB3B',
              '#795548'
            ],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 500
          },
          legend: {
            position: 'bottom',
            labels: {
              padding: 10,
              fontSize: 11
            }
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                return label + ': ' + value + ' requests';
              }
            }
          }
        }
      });
    },

    renderWorkflowChart() {
      if (!this.$refs.workflowChart) return;

      // Destroy existing chart
      if (this.charts.workflow) {
        this.charts.workflow.destroy();
        this.charts.workflow = null;
      }

      const ctx = this.$refs.workflowChart.getContext('2d');
      const topWorkflows = this.workflowBreakdown.slice(0, 5);

      if (!topWorkflows.length) return;

      this.charts.workflow = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: topWorkflows.map(d => d.workflow),
          datasets: [{
            label: 'Total Cost (R)',
            data: topWorkflows.map(d => d.totalCost),
            backgroundColor: '#4CAF50',
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 500
          },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return 'R' + value.toFixed(2);
                }
              }
            }],
            yAxes: [{
              ticks: {
                fontSize: 11
              }
            }]
          },
          legend: {
            display: false
          }
        }
      });
    }
  },
  computed: {
    ...mapGetters('userInfo', ['userInfo']),
    
    isDarkMode() {
      return this.$store.state.darkTheme
    },

    isMasterAdmin() {
      return (this.userInfo.username.match(/@tasklogix\.co\.za$/gi) && this.userInfo.tasklogixAdmin) || this.userInfo.username.match(/^tlx@/i);
    },

    summaryStats() {
      if (!this.stats.length) return {
        totalRequests: 0,
        totalTokens: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalCost: 0,
        actualTotalCost: 0,
        totalMarkup: 0,
        avgCostPerRequest: 0,
        avgMarkupPercent: 0
      };

      const totalRequests = this.stats.length;
      const inputTokens = this.stats.reduce((sum, s) => sum + (s.input || 0), 0);
      const outputTokens = this.stats.reduce((sum, s) => sum + (s.output || 0), 0);
      const totalTokens = inputTokens + outputTokens;
      const totalCost = this.stats.reduce((sum, s) => sum + (s.totalCost || 0), 0);
      const actualTotalCost = this.stats.reduce((sum, s) => sum + (s.actualCost || 0), 0);
      const totalMarkup = this.stats.reduce((sum, s) => sum + (s.markup || 0), 0);
      const avgCostPerRequest = totalCost / totalRequests;
      const avgMarkupPercent = actualTotalCost > 0 ? (totalMarkup / actualTotalCost) * 100 : 0;

      return {
        totalRequests,
        totalTokens,
        inputTokens,
        outputTokens,
        totalCost,
        actualTotalCost,
        totalMarkup,
        avgCostPerRequest,
        avgMarkupPercent
      };
    },

    ecosystemBreakdown() {
      const grouped = {};
      this.stats.forEach(item => {
        const eco = item.ecosystem;
        if (!grouped[eco]) {
          grouped[eco] = { ecosystem: eco, requests: 0, tokens: 0, cost: 0 };
        }
        grouped[eco].requests++;
        grouped[eco].tokens += (item.input || 0) + (item.output || 0);
        grouped[eco].cost += item.totalCost || 0;
      });
      return Object.values(grouped).sort((a, b) => b.cost - a.cost);
    },

    userBreakdown() {
      return this.aggregateBy('user');
    },

    modelBreakdown() {
      const grouped = {};
      this.stats.forEach(item => {
        const key = `${item.model}__${item.ecosystem}`;
        if (!grouped[key]) {
          grouped[key] = {
            model: item.model,
            ecosystem: item.ecosystem,
            requests: 0,
            inputTokens: 0,
            outputTokens: 0,
            totalTokens: 0,
            inputCost: 0,
            outputCost: 0,
            totalCost: 0,
            actualCost: 0,
            markup: 0
          };
        }
        grouped[key].requests++;
        grouped[key].inputTokens += item.input || 0;
        grouped[key].outputTokens += item.output || 0;
        grouped[key].totalTokens += (item.input || 0) + (item.output || 0);
        grouped[key].inputCost += item.inputCost || 0;
        grouped[key].outputCost += item.outputCost || 0;
        grouped[key].totalCost += item.totalCost || 0;
        grouped[key].actualCost += item.actualCost || 0;
        grouped[key].markup += item.markup || 0;
      });
      return Object.values(grouped).sort((a, b) => b.totalCost - a.totalCost);
    },

    workflowBreakdown() {
      return this.aggregateBy('workflow');
    },

    userHeaders() {
      const baseHeaders = [
        { text: 'User', value: 'user', align: 'left',  },
        { text: 'Requests', value: 'requests', align: 'right' },
        { text: 'Input Tokens', value: 'inputTokens', align: 'right' },
        { text: 'Output Tokens', value: 'outputTokens', align: 'right' },
        { text: 'Total Tokens', value: 'totalTokens', align: 'right' },
        { text: 'Input Cost', value: 'inputCost', align: 'right' },
        { text: 'Output Cost', value: 'outputCost', align: 'right' },
        { text: 'Total Cost', value: 'totalCost', align: 'right' }
      ];
      if (this.isMasterAdmin && this.adminFields) {
        baseHeaders.push(
          { text: 'Actual Cost', value: 'actualCost', align: 'right' },
          { text: 'Markup', value: 'markup', align: 'right' }
        );
      }
      return baseHeaders;
    },

    modelHeaders() {
      const baseHeaders = [
        { text: 'Model', value: 'model', align: 'left' }
      ];

      if (this.isMasterAdmin && this.adminFields) {
        baseHeaders.push({ text: 'Ecosystem', value: 'ecosystem', align: 'left' });
      }

      baseHeaders.push(
        { text: 'Requests', value: 'requests', align: 'right' },
        { text: 'Input Tokens', value: 'inputTokens', align: 'right' },
        { text: 'Output Tokens', value: 'outputTokens', align: 'right' },
        { text: 'Total Tokens', value: 'totalTokens', align: 'right' },
        { text: 'Input Cost', value: 'inputCost', align: 'right' },
        { text: 'Output Cost', value: 'outputCost', align: 'right' },
        { text: 'Total Cost', value: 'totalCost', align: 'right' }
      );

      if (this.isMasterAdmin && this.adminFields) {
        baseHeaders.push(
          { text: 'Actual Cost', value: 'actualCost', align: 'right' },
          { text: 'Markup', value: 'markup', align: 'right' }
        );
      }
      return baseHeaders;
    },

    workflowHeaders() {
      const baseHeaders = [
        { text: 'Workflow', value: 'workflow', align: 'left' },
        { text: 'Requests', value: 'requests', align: 'right' },
        { text: 'Input Tokens', value: 'inputTokens', align: 'right' },
        { text: 'Output Tokens', value: 'outputTokens', align: 'right' },
        { text: 'Total Tokens', value: 'totalTokens', align: 'right' },
        { text: 'Input Cost', value: 'inputCost', align: 'right' },
        { text: 'Output Cost', value: 'outputCost', align: 'right' },
        { text: 'Total Cost', value: 'totalCost', align: 'right' }
      ];
      if (this.isMasterAdmin && this.adminFields) {
        baseHeaders.push(
          { text: 'Actual Cost', value: 'actualCost', align: 'right' },
          { text: 'Markup', value: 'markup', align: 'right' }
        );
      }
      return baseHeaders;
    },

    ecosystemHeaders() {
      return [
        { text: 'Ecosystem', value: 'ecosystem', align: 'left' },
        { text: 'Requests', value: 'requests', align: 'right' },
        { text: 'Tokens', value: 'tokens', align: 'right' },
        { text: 'Cost', value: 'cost', align: 'right' }
      ];
    },

    transactionHeaders() {
      const baseHeaders = [
        { text: 'Date', value: 'createdAt', align: 'left', },
        { text: 'User', value: 'user', align: 'left', },
        { text: 'Workflow', value: 'workflow', align: 'left' },
        { text: 'Ecosystem', value: 'ecosystem', align: 'left' },
        { text: 'Model', value: 'model', align: 'left' },
        { text: 'Input', value: 'input', align: 'right' },
        { text: 'Output', value: 'output', align: 'right' },
        { text: 'Input Cost', value: 'inputCost', align: 'right' },
        { text: 'Output Cost', value: 'outputCost', align: 'right' },
        { text: 'Total Cost', value: 'totalCost', align: 'right' }
      ];
      if (this.isMasterAdmin && this.adminFields) {
        baseHeaders.push(
          { text: 'Actual Cost', value: 'actualCost', align: 'right' },
          { text: 'Markup', value: 'markup', align: 'right' }
        );
      }
      return baseHeaders;
    }
  },
  filters: {
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat().format(Math.round(value));
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    formatPercent(value) {
      if (!value && value !== 0) return '0%';
      return `${parseFloat(value).toFixed(1)}%`;
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      return format(date, 'YYYY-MM-DD HH:mm');
    },
    formatDateShort(value) {
      if (!value) return '';
      const date = typeof value === 'string' ? parse(value) : value;
      return format(date, 'MMM DD, YYYY');
    }
  }
}
</script>

<style scoped>

</style>